import React, { useState, useCallback, useEffect } from 'react';
import { EditorState } from 'draft-js';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import SidePanelTextEditor from 'Dashboard/components/SidePanelTextEditor/SidePanelTextEditor';
import {
  editorStateToJsonString,
  isEmptyEditor,
  jsonContentStateFromString,
  editorStateFromJson,
} from 'Shared/utils/draftJsHelpers';
import { getFeedbackCommentByUuid } from 'Shared/selectors/FeedbackComments.selectors';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { modifyUrlBySmartLink } from 'Shared/components/DraftEditor/plugins/smartLinksPlugin';
import { createFeedbackComment, updateFeedbackComment } from 'Shared/actions/FeedbackComments.actions';
import { enrichEditorStateByImageAttachment } from 'Shared/components/DraftEditor/plugins/imageAttachmentsPlugin';
import {
  enrichEditorStateByGif,
  updateEntitysOfGifBeforeSend,
} from 'Shared/components/DraftEditor/plugins/addGifPlugin';
import { enrichEditorStateByEmoji } from 'Shared/components/DraftEditor/plugins/addEmojiPlugin';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  feedbackUuid: string;
  workspaceUuid: string;
  commentUuid?: string;
  onCommentEditorClose: Function;
};

function FeedbackCommentEditor({ feedbackUuid, workspaceUuid, onCommentEditorClose, commentUuid }: Props) {
  const dispatch = useDispatch();
  const commentForUpdate = useDeepEqualSelector(getFeedbackCommentByUuid(commentUuid));
  const { user: currentUser } = useCurrentUser();

  const [editorState, setEditorState] = useState(
    commentForUpdate ? editorStateFromJson(commentForUpdate.body) : EditorState.createEmpty()
  );

  useEffect(() => {
    if (commentForUpdate?.body && commentForUpdate?.body !== editorStateToJsonString(editorState))
      setEditorState(editorStateFromJson(commentForUpdate.body));
  }, [commentForUpdate?.body]);

  const setState = useCallback(
    state => {
      if (typeof state === 'function') {
        setEditorState(state({ editorState }).editorState);
      } else {
        setEditorState(state.editorState);
      }
    },
    [editorState]
  );

  const handleEditorChange = useCallback(editorStateValue => modifyUrlBySmartLink(editorStateValue, { setState }), []);

  const handleCommentCreateClick = useCallback(async () => {
    if (isEmptyEditor(editorState)) return;
    const uuid = uuidv4();
    const body = editorStateToJsonString(updateEntitysOfGifBeforeSend(editorState));
    onCommentEditorClose();
    await dispatch(
      createFeedbackComment({ uuid, body, feedbackUuid, workspaceUuid, viewerIds: [currentUser.id], user: currentUser })
    );
  }, [currentUser, dispatch, editorState, feedbackUuid, onCommentEditorClose, workspaceUuid]);

  const handleAttachmentInputChange = useCallback(
    async file => {
      const state = await enrichEditorStateByImageAttachment(editorState, file);
      setEditorState(state);
    },
    [editorState]
  );

  const handleGifAdd = useCallback(gif => setEditorState(enrichEditorStateByGif(editorState, gif)), [editorState]);

  const handleEmojiAdd = useCallback(emoji => setEditorState(enrichEditorStateByEmoji(editorState, emoji)), [
    editorState,
  ]);

  const handleCommentUpdate = useCallback(async () => {
    await dispatch(
      updateFeedbackComment({
        uuid: commentUuid,
        feedbackUuid,
        body: editorStateToJsonString(updateEntitysOfGifBeforeSend(editorState)),
      })
    );
    onCommentEditorClose();
  }, [dispatch, feedbackUuid, commentUuid, editorState, onCommentEditorClose]);

  const handleCreateVideoComment = useCallback(
    video => {
      const body = jsonContentStateFromString(video.audioOnly ? 'New Audio Item' : 'New Video Item');
      const uuid = uuidv4();
      onCommentEditorClose();
      dispatch(
        createFeedbackComment({
          uuid,
          body,
          feedbackUuid,
          workspaceUuid,
          video,
          viewerIds: [currentUser.id],
          user: currentUser,
        })
      );
    },
    [currentUser, dispatch, feedbackUuid, onCommentEditorClose, workspaceUuid]
  );

  const handleCancelClick = useCallback(() => onCommentEditorClose(), [onCommentEditorClose]);

  return (
    <SidePanelTextEditor
      meetingItemUuid={null}
      editorState={editorState}
      onEditorChange={handleEditorChange}
      onAttachmentInputChange={handleAttachmentInputChange}
      onSaveVideo={handleCreateVideoComment}
      onAssignedPersonAdd={() => null}
      onSubmit={handleCommentCreateClick}
      onGifAdd={handleGifAdd}
      onEmojiAdd={handleEmojiAdd}
      onCancel={handleCancelClick}
      commentForUpdate={commentForUpdate}
      onUpdateComment={handleCommentUpdate}
    />
  );
}

export default FeedbackCommentEditor;
